import React, { useState, useEffect } from 'react';
import { Package, Plus, RefreshCw, Filter, CheckCircle, AlertTriangle, Clock } from 'lucide-react';
import Header from '../../components/navigation/Header';
import GlobalFilterBar from '../../components/navigation/GlobalFilterBar';
import DataRefreshIndicator from '../../components/navigation/DataRefreshIndicator';
import LegalDisclaimerBanner from '../../components/navigation/LegalDisclaimerBanner';
import ComplianceAlertBanner from '../../components/navigation/ComplianceAlertBanner';
import Button from '../../components/ui/Button';
import documentRenewalBatchService from '../../services/documentRenewalBatchService';
import BatchStatisticsCard from './components/BatchStatisticsCard';
import BatchListTable from './components/BatchListTable';
import CreateBatchModal from './components/CreateBatchModal';
import BatchDetailsModal from './components/BatchDetailsModal';




const DocumentRenewalBatchCoordinator = () => {
  // State
  const [batches, setBatches] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  // Filters
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedPriority, setSelectedPriority] = useState('all');

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);

  // Global filter handler
  const handleGlobalFilterChange = (filters) => {
    console.log('Global filters changed:', filters);
  };

  useEffect(() => {
    loadData();

    // Subscribe to real-time updates
    const channel = documentRenewalBatchService?.subscribeToRenewalBatches(() => {
      loadData();
    });

    return () => {
      documentRenewalBatchService?.unsubscribeFromRenewalBatches(channel);
    };
  }, []);

  useEffect(() => {
    applyFilters();
  }, [selectedStatus, selectedPriority]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      const [batchesData, statsData] = await Promise.all([
        documentRenewalBatchService?.getAllRenewalBatches(),
        documentRenewalBatchService?.getBatchStatistics()
      ]);

      setBatches(batchesData || []);
      setStatistics(statsData);
    } catch (err) {
      console.error('Error loading data:', err);
      setError(err?.message || 'Failed to load renewal batch data');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = async () => {
    try {
      const filters = {};

      if (selectedStatus !== 'all') {
        filters.batchStatus = selectedStatus;
      }

      if (selectedPriority !== 'all') {
        filters.batchPriority = selectedPriority;
      }

      const data = await documentRenewalBatchService?.getAllRenewalBatches(filters);
      setBatches(data || []);
    } catch (err) {
      console.error('Error applying filters:', err);
    }
  };

  const handleAutoCreateBatches = async () => {
    try {
      setRefreshing(true);
      setError('');
      setSuccess('');

      await documentRenewalBatchService?.autoCreateRenewalBatches();

      setSuccess('Renewal batches created successfully based on expiring documents');
      await loadData();

      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      console.error('Error auto-creating batches:', err);
      setError(err?.message || 'Failed to create renewal batches');
      setTimeout(() => setError(''), 5000);
    } finally {
      setRefreshing(false);
    }
  };

  const handleCreateBatch = async (batchData) => {
    try {
      setError('');
      setSuccess('');

      await documentRenewalBatchService?.createRenewalBatch(batchData);

      setSuccess('Renewal batch created successfully');
      setShowCreateModal(false);
      await loadData();

      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error creating batch:', err);
      setError(err?.message || 'Failed to create renewal batch');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleViewBatch = (batch) => {
    setSelectedBatch(batch);
    setShowDetailsModal(true);
  };

  const handleSubmitForReview = async (batchId) => {
    try {
      setError('');
      setSuccess('');

      await documentRenewalBatchService?.submitBatchForReview(batchId, 'current_user@example.com');

      setSuccess('Batch submitted for review successfully');
      await loadData();

      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error submitting batch:', err);
      setError(err?.message || 'Failed to submit batch for review');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleApproveBatch = async (batchId) => {
    try {
      setError('');
      setSuccess('');

      await documentRenewalBatchService?.approveBatch(batchId, 'current_user@example.com');

      setSuccess('Batch approved successfully');
      await loadData();

      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error approving batch:', err);
      setError(err?.message || 'Failed to approve batch');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleStartProcessing = async (batchId) => {
    try {
      setError('');
      setSuccess('');

      await documentRenewalBatchService?.startBatchProcessing(batchId);

      setSuccess('Batch processing started');
      await loadData();

      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error starting processing:', err);
      setError(err?.message || 'Failed to start batch processing');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleDeleteBatch = async (batchId) => {
    if (!window.confirm('Are you sure you want to delete this batch?')) {
      return;
    }

    try {
      setError('');
      setSuccess('');

      await documentRenewalBatchService?.deleteBatch(batchId);

      setSuccess('Batch deleted successfully');
      await loadData();

      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error deleting batch:', err);
      setError(err?.message || 'Failed to delete batch');
      setTimeout(() => setError(''), 3000);
    }
  };

  const statusCategories = [
    { id: 'all', label: 'All Batches', icon: Package, color: 'gray' },
    { id: 'draft', label: 'Draft', icon: Clock, color: 'gray' },
    { id: 'pending_review', label: 'Pending Review', icon: AlertTriangle, color: 'yellow' },
    { id: 'approved', label: 'Approved', icon: CheckCircle, color: 'green' },
    { id: 'processing', label: 'Processing', icon: RefreshCw, color: 'blue' },
    { id: 'completed', label: 'Completed', icon: CheckCircle, color: 'emerald' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <GlobalFilterBar onFilterChange={handleGlobalFilterChange} />
        <DataRefreshIndicator />
        <LegalDisclaimerBanner />
        <ComplianceAlertBanner />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading renewal batch data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <GlobalFilterBar onFilterChange={handleGlobalFilterChange} />
      <DataRefreshIndicator />
      <LegalDisclaimerBanner />
      <ComplianceAlertBanner />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Document Renewal Batch Coordinator</h1>
              <p className="mt-2 text-gray-600">
                Consolidate expiring documents into coordinated renewal batches with automated processing workflows
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <Button
                onClick={handleAutoCreateBatches}
                disabled={refreshing}
                className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white"
              >
                <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
                <span>{refreshing ? 'Creating...' : 'Auto-Create Batches'}</span>
              </Button>
              <Button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center space-x-2 bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                <Plus className="h-4 w-4" />
                <span>Create Batch</span>
              </Button>
            </div>
          </div>
        </div>

        {/* Success/Error Messages */}
        {success && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
            <p className="text-green-800">{success}</p>
          </div>
        )}

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* Statistics Cards */}
        {statistics && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <BatchStatisticsCard
              title="Total Batches"
              value={statistics?.totalBatches}
              icon={Package}
              color="blue"
            />
            <BatchStatisticsCard
              title="Processing"
              value={statistics?.processingBatches}
              icon={RefreshCw}
              color="yellow"
            />
            <BatchStatisticsCard
              title="Completed"
              value={statistics?.completedBatches}
              icon={CheckCircle}
              color="green"
            />
            <BatchStatisticsCard
              title="Critical Priority"
              value={statistics?.criticalBatches}
              icon={AlertTriangle}
              color="red"
            />
          </div>
        )}

        {/* Status Filter */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          {statusCategories?.map(category => {
            const IconComponent = category?.icon;
            return (
              <button
                key={category?.id}
                onClick={() => setSelectedStatus(category?.id)}
                className={`p-4 rounded-lg border-2 transition-all ${
                  selectedStatus === category?.id
                    ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-center mb-2">
                  <IconComponent className={`h-6 w-6 text-${category?.color}-600`} />
                </div>
                <p className="text-sm font-medium text-gray-900 text-center">{category?.label}</p>
              </button>
            );
          })}
        </div>

        {/* Batch List */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Renewal Batches</h2>
          </div>
          <BatchListTable
            batches={batches}
            onViewBatch={handleViewBatch}
            onSubmitForReview={handleSubmitForReview}
            onApproveBatch={handleApproveBatch}
            onStartProcessing={handleStartProcessing}
            onDeleteBatch={handleDeleteBatch}
          />
        </div>
      </div>

      {/* Modals */}
      {showCreateModal && (
        <CreateBatchModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateBatch}
        />
      )}

      {showDetailsModal && selectedBatch && (
        <BatchDetailsModal
          batch={selectedBatch}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedBatch(null);
          }}
          onRefresh={loadData}
        />
      )}
    </div>
  );
};

export default DocumentRenewalBatchCoordinator;